import { createContext, useContext, useEffect, useState } from "react";

const initialState = {
  theme: "light",
  setTheme: () => null,
};

const ThemeProviderContext = createContext(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "vite-ui-theme",
  ...props
}) {
  const getInitialTheme = () => {
    if (typeof window === "undefined") return defaultTheme;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) return stored;
      // first load in a brand-new tab/window → default to light
      sessionStorage.setItem(storageKey, defaultTheme);
      return defaultTheme;
    } catch {
      return defaultTheme;
    }
  };

  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    const applied =
      theme === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : theme;

    root.classList.add(applied);

    // persist current choice for this *tab only*
    try {
      sessionStorage.setItem(storageKey, theme);
    } catch {}
  }, [theme, storageKey]);

  // If using "system", react to OS theme changes while this tab is open
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(mql.matches ? "dark" : "light");
    };
    mql.addEventListener?.("change", apply);
    return () => mql.removeEventListener?.("change", apply);
  }, [theme]);

  const value = {
    theme,
    setTheme: (next) => {
      setThemeState(next); // sessionStorage updated by the effect above
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
